import React, { Component } from "react";
import { addMilliseconds, subMilliseconds } from "date-fns";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { logger } from "@formant/core/common/logger";
import { duration } from "@formant/core/model/duration";
import { INumericViewConfiguration } from "@formant/core/model/INumericViewConfiguration";
import { IStreamData } from "@formant/core/model/IStreamData";
import { Uuid } from "@formant/core/model/Uuid";

import { observeStore, queryStore } from "../../stores";
import { BatteryIcon } from "../common/BatteryIcon";
import { DeviceSelector } from "../common/DeviceSelector";
import { DeviceTags } from "../common/DeviceTags";
import * as styles from "./BatteryModule.scss";
import useModuleMemory from "./hooks/UseModuleMemory";
import { IBaseModuleProps } from "./IBaseModuleProps";
import { Module, ModuleSize } from "./Module";
import { ModuleFooter } from "./ModuleFooter";
import { INumericSetFullEntry, NumericSet } from "./NumericSet";
import { currentByDevice } from "./utils/currentByDevice";
import { findConfiguration } from "./utils/findConfiguration";

@observer
export class BatteryModule extends Component<IBaseModuleProps> {
    @observable
    private selectedDeviceId?: Uuid;
    @observable
    private size?: ModuleSize;
    private data: IStreamData<"battery">[] = [];

    @observable
    private span = 1 * duration.minute;

    @observable
    private useModuleMemory = useModuleMemory();

    public componentDidMount() {
        const { streamName, configuration } = this.props;

        // check if span value exists in local storage
        const serializedState = this.useModuleMemory.rehydrate(streamName);

        if (serializedState) {
            const { span } = serializedState;
            this.span = span;
        } else {
            const { span } =
                findConfiguration(configuration, streamName, "battery") || {};
            if (span) {
                this.span = span;
            }
        }
        if (typeof this.span !== "number") {
            logger.warn("BatteryModule: span is not a number", {
                span: this.span,
            });
            this.span = 1 * duration.minute;
        }
    }

    public render() {
        const { selectedDeviceId, size, onSelectDevice } = this;
        const {
            streamName,
            filter,
            hovering,
            hideDevice,
            fullscreen,
            focused,
            onSelectTime,
            layoutModule,
        } = this.props;
        const { time, mode } = observeStore;

        const span = this.span;
        const showOnlyCurrentValues =
            layoutModule?.configuration?.showOnlyCurrentValues;

        const live = mode === "live";

        const startTime = subMilliseconds(time, (span * 3) / 4);
        const endTime = addMilliseconds(time, span / 4);

        const queryData = queryStore.moduleQuery(
            filter,
            streamName,
            "battery",
            startTime,
            endTime,
        );

        const loading = queryData === undefined;
        const tooMuchData = queryData === "too much data";

        this.data = (queryData !== "too much data" && queryData) || this.data;
        const data = this.data;

        const empty = data.length === 0;

        const currentPoints = currentByDevice(data, time);

        const active = currentPoints.find(
            ({ deviceId }) => selectedDeviceId === deviceId,
        );

        const deviceIds = currentPoints.map(_ => _.deviceId);

        const all = data.filter(_ => _.deviceId === selectedDeviceId);

        const percentageHistory: [number, number][] = [];
        const voltageHistory: [number, number][] = [];
        const currentHistory: [number, number][] = [];
        const chargeHistory: [number, number][] = [];

        for (const { points } of all) {
            for (const [pointTime, value] of points) {
                const { charge, voltage, current, percentage } = value;
                if (charge) {
                    chargeHistory.push([pointTime, charge]);
                }
                if (voltage) {
                    voltageHistory.push([pointTime, voltage]);
                }
                if (current) {
                    currentHistory.push([pointTime, current]);
                }
                if (percentage) {
                    percentageHistory.push([pointTime, percentage]);
                }
            }
        }

        const entries: INumericSetFullEntry[] = [
            {
                label: "Charged",
                unit: "%",
                value: active ? active?.value.percentage : undefined,
                history: percentageHistory,
            },
        ];

        if (voltageHistory.length > 0) {
            entries.push({
                label: "Voltage",
                unit: "V",
                value: active?.value.voltage,
                history: voltageHistory,
            });
        }

        if (currentHistory.length > 0) {
            entries.push({
                label: "Current",
                unit: "A",
                value: active?.value.current,
                history: currentHistory,
            });
        }

        if (chargeHistory.length > 0) {
            entries.push({
                label: "Charge",
                unit: "Ah",
                value: active?.value.charge,
                history: chargeHistory,
            });
        }

        const icon = active && (
            <BatteryIcon
                value={active.value}
                className={styles.icon}
                large={true}
            />
        );

        return (
            <Module
                {...this.props}
                type="battery"
                icon={icon}
                loading={loading}
                empty={empty}
                tooMuchData={tooMuchData}
                onChangeSize={this.onChangeSize}
                className={styles["battery-module"]}
                onResetTimeSpan={this.onResetTimeSpan}
            >
                {selectedDeviceId && (
                    <NumericSet
                        deviceId={selectedDeviceId}
                        time={time}
                        live={live}
                        startTime={startTime}
                        endTime={endTime}
                        entries={entries}
                        size={size}
                        focused={focused}
                        invertedWarning={true}
                        onSelectTime={onSelectTime}
                        span={span}
                        onChangeSpan={this.onChangeSpan}
                        showOnlyCurrentValues={showOnlyCurrentValues || false}
                    />
                )}
                <ModuleFooter visible={hovering} floating={true}>
                    {active && selectedDeviceId && !hideDevice && (
                        <DeviceTags
                            deviceId={selectedDeviceId}
                            tags={active.tags}
                        />
                    )}
                    <DeviceSelector
                        selected={selectedDeviceId}
                        deviceIds={deviceIds}
                        onSelect={onSelectDevice}
                        fullscreen={fullscreen}
                    />
                </ModuleFooter>
            </Module>
        );
    }

    private changeConfiguration(updates: Partial<INumericViewConfiguration>) {
        const { streamName, onChangeConfiguration } = this.props;

        // save to local storage
        this.useModuleMemory.saveState(streamName, updates);

        if (!onChangeConfiguration) {
            return;
        }
        onChangeConfiguration({
            streamName,
            type: "battery",
            updates,
        });
    }

    private onResetTimeSpan = () => {
        this.span = 1 * duration.minute;
        this.changeConfiguration({ span: this.span });
    };

    private onChangeSpan = (span: number) => {
        this.span = span;
        this.changeConfiguration({ span });
    };

    private onSelectDevice = (deviceId: Uuid) => {
        this.selectedDeviceId = deviceId;
    };

    private onChangeSize = (size: ModuleSize) => {
        this.size = size;
    };
}
